/**
 * Add Inspection Dialog - New Implementation
 *
 * Add inspection from template library.
 * Simplified without language dictionary.
 */
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { Modal, Button, Select } from "@gpstrackit/gtc-react-controls";
import { v4 as uuidv4 } from "uuid";

// Hardcoded inspection type templates
const INSPECTION_TEMPLATES = [
	{
		id: "pre-trip-template",
		name: "Pre-Trip",
		template: {
			typeId: uuidv4(),
			detailType: "Pre-Trip",
			description: "Inspect the vehicle before driving according to DOT and company policy.",
			inspectionType: "pre-trip",
			enforceOrder: true,
			requiresReview: true,
			legallyMandated: true,
			prepopulateDefects: true,
			associateDriver: true,
			zoneless: false,
			formData: [
				{
					fieldId: uuidv4(),
					fieldName: "Odometer",
					fieldType: "int",
					hint: "Value reported on the odometer",
					required: true,
				},
				{
					fieldId: uuidv4(),
					fieldName: "Engine Hours",
					fieldType: "int",
					hint: "Engine hours as reported on dash",
					required: false,
				},
				{
					fieldId: uuidv4(),
					fieldName: "Fuel Level",
					fieldType: "int",
					hint: "Estimated fuel in tank",
					required: false,
				},
			],
		},
	},
	{
		id: "post-trip-template",
		name: "Post-Trip",
		template: {
			typeId: uuidv4(),
			detailType: "Post-Trip",
			description: "Inspect the vehicle after driving according to DOT and company policy.",
			inspectionType: "post-trip",
			enforceOrder: true,
			requiresReview: true,
			legallyMandated: true,
			prepopulateDefects: true,
			associateDriver: true,
			zoneless: false,
			formData: [
				{
					fieldId: uuidv4(),
					fieldName: "Odometer",
					fieldType: "int",
					hint: "Value reported on the odometer",
					required: true,
				},
				{
					fieldId: uuidv4(),
					fieldName: "Engine Hours",
					fieldType: "int",
					hint: "Engine hours as reported on dash",
					required: false,
				},
				{
					fieldId: uuidv4(),
					fieldName: "Post trip notes",
					fieldType: "text",
					hint: "Trip notes or concerns",
					required: false,
				},
			],
		},
	},
	{
		id: "time-card-template",
		name: "Time Card",
		template: {
			typeId: uuidv4(),
			detailType: "Time Card",
			description: "Record your time by clocking in or out.",
			inspectionType: "other",
			enforceOrder: false,
			requiresReview: false,
			legallyMandated: false,
			prepopulateDefects: true,
			associateDriver: true,
			zoneless: true,
			formData: [
				{
					fieldId: uuidv4(),
					fieldName: "Clock",
					fieldType: "select",
					hint: "Punch time card in or out",
					required: true,
				},
				{
					fieldId: uuidv4(),
					fieldName: "Route Number",
					fieldType: "int",
					hint: "Route number",
					required: false,
				},
			],
		},
	},
	{
		id: "load-securement-template",
		name: "Load Securement",
		template: {
			typeId: uuidv4(),
			detailType: "Load Securement",
			description: "Inspect tarps, straps, and other load safety devices.",
			inspectionType: "other",
			enforceOrder: false,
			requiresReview: false,
			legallyMandated: false,
			prepopulateDefects: true,
			associateDriver: true,
			zoneless: false,
			formData: [],
		},
	},
	{
		id: "child-check-template",
		name: "Child Check",
		template: {
			typeId: uuidv4(),
			detailType: "Child Check",
			description: "Check behind and under seats for any remaining children.",
			inspectionType: "other",
			enforceOrder: true,
			requiresReview: false,
			legallyMandated: false,
			prepopulateDefects: true,
			associateDriver: true,
			zoneless: true,
			formData: [],
		},
	},
];

/**
 * Dialog for adding an inspection type from the template library
 */
const AddInspectionDialog = ({ open, onClose, onAdd }) => {
	const { messages } = useIntl();
	const [selectedTemplate, setSelectedTemplate] = useState("");

	const handleAdd = () => {
		const template = INSPECTION_TEMPLATES.find((t) => t.id === selectedTemplate);
		if (template) {
			// Generate new UUIDs for the inspection and its form fields
			const newInspection = {
				...template.template,
				typeId: uuidv4(),
				formData: template.template.formData.map((field) => ({
					...field,
					fieldId: uuidv4(),
				})),
			};
			onAdd(newInspection);
			handleClose();
		}
	};

	const handleClose = () => {
		setSelectedTemplate("");
		onClose();
	};

	return (
		<Modal
			title={messages.addInspectionType || "Add Inspection Type"}
			open={open}
			handleClose={handleClose}
			size="small"
			footer={
				<div>
					<Button
						variant="secondary"
						onClick={handleClose}
						style={{ marginRight: "5px" }}
					>
						{messages.cancel || "Cancel"}
					</Button>
					<Button
						variant="primary"
						onClick={handleAdd}
						isDisabled={!selectedTemplate}
					>
						{messages.apply || "Apply"}
					</Button>
				</div>
			}
		>
			<div style={{ padding: "15px" }}>
				<Select
					id="inspection-template-select"
					label={messages.inspectionTypeRequired || "Inspection Type *"}
					placeholder={messages.selectInspectionType || "Select Inspection Type"}
					value={INSPECTION_TEMPLATES.find((t) => t.id === selectedTemplate) || null}
					onChange={(selected) => setSelectedTemplate(selected?.id || "")}
					options={INSPECTION_TEMPLATES}
					textField="name"
					valueField="id"
					required
				/>
			</div>
		</Modal>
	);
};

export default AddInspectionDialog;
